import { FC, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Text,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Button,
  Tooltip,
  Avatar,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { NewDataGrid } from '../../../NewGridTable/NewDataGrid';
import { apiClient, getToken, getOrgId } from '@devd-client/api';
import { useGlobalState } from '../../../hooks/useGlobalState';
import { MetricModalLoading } from './DefaultDetailsModal/MetricModalLoading';
import type { AverageActiveDaysDetailItem } from '../../types/charts.types';

interface ActiveDaysContributorData {
  data: AverageActiveDaysDetailItem[];
}

interface ActiveDaysDetailsModalProps {
  openDrawer: boolean;
  handleDrawerClose: () => void;
  team: string;
  contributorData?: ActiveDaysContributorData;
  isFetching: boolean;
  subHeading: string;
  startDate?: string;
  endDate?: string;
}

//This sidebar is used to show the details of active dev days metric in git dashboard
export const ActiveDaysDetailsModal: FC<ActiveDaysDetailsModalProps> = ({
  openDrawer,
  handleDrawerClose,
  team,
  contributorData,
  isFetching,
  subHeading,
  startDate,
  endDate,
}) => {
  const rows: AverageActiveDaysDetailItem[] = useMemo(
    () => contributorData?.data || [],
    [contributorData?.data]
  );

  const [pending, setPending] = useState(false);
  const { globalState } = useGlobalState();
  const downloadAllowed = globalState?.dto?.downloadAllowed;

  const columns: ColumnDef<AverageActiveDaysDetailItem, any>[] = useMemo(
    () => [
      {
        header: 'Email',
        accessorKey: 'email',
        size: 300,
        enableSorting: false,
        cell: ({ row }) => (
          <Flex align="center">
            <Avatar size="xs" name={row.original?.email} />
            <Link to={`/contributors/${row.original?.email}`}>
              <Text
                ml={2}
                fontSize="sm"
                fontFamily="heading"
                color="primary.500"
                fontWeight="medium"
                textDecoration="underline"
                noOfLines={1}
              >
                {row.original?.email}
              </Text>
            </Link>
          </Flex>
        ),
        meta: {
          isNumeric: false,
        },
      },
      {
        header: 'Active Days',
        accessorKey: 'activeDays',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => (
          <Text
            fontSize="sm"
            fontFamily="heading"
            color="text.secondary"
            fontWeight="medium"
          >
            {row.original?.activeDays}
          </Text>
        ),
      },
    ],
    []
  );

  const download = function (data: any) {
    const blob = new Blob([data], { type: 'text/csv' });
    const url = window?.URL?.createObjectURL(blob);

    const a = document?.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute(
      'download',
      `${getOrgId()} - ${team === '' ? 'Organisation' : team}`
    );
    a.click();
  };

  const downloadCSV = async () => {
    setPending(true);
    apiClient(`/v1/csv/contributors-active-days`, {
      method: 'POST',
      headers: {
        authorization: `bearer ${getToken()}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orgId: getOrgId(),
        startDate: startDate,
        endDate: endDate,
        teamId: team,
      }),
    })
      .then((res: any) => {
        setPending(false);
        download(res);
      })
      .catch((err) => {
        setPending(false);
      });
  };

  return (
    <Modal
      isOpen={openDrawer}
      onClose={handleDrawerClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent minW="4xl" maxW="4xl">
        <ModalHeader pb={2}>
          <Flex justify="space-between" align="flex-start" gap={4}>
            <Stack spacing={1}>
              <Text
                fontFamily="heading"
                fontSize="lg"
                fontWeight="bold"
                color="text.primary"
              >
                Average Active Days
              </Text>
              <Text
                fontFamily="heading"
                fontSize="sm"
                color="text.secondary"
                fontWeight="medium"
              >
                {subHeading}
              </Text>
            </Stack>
            <Tooltip
              hasArrow
              label={!downloadAllowed ? 'Upgrade plan to download data' : ''}
              isDisabled={downloadAllowed}
            >
              <Button
                variant="tertiary"
                size="sm"
                h="32px"
                px={4}
                borderRadius="full"
                onClick={downloadCSV}
                isLoading={pending}
                isDisabled={!downloadAllowed}
                minW="120px"
                mr={6}
              >
                Download Data
              </Button>
            </Tooltip>
          </Flex>
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            {isFetching && !contributorData?.data ? (
              <MetricModalLoading />
            ) : rows && rows.length > 0 ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
              >
                <NewDataGrid
                  data={rows}
                  columns={columns}
                  showLoader={false}
                  skeletonLoader={false}
                  sticky="none"
                  useMaxH
                  showGridLines
                />
              </Box>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  No data found
                </Text>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ActiveDaysDetailsModal;
